import { ModelBase } from "./ModelBase";

export interface DataSourceBase<TId> extends ModelBase<TId> {
  /**
   * Unique code to distinguish a data source.
   * Will not always be set due to custom data sources.
   */
  code?: DataSourceCode;
  name: string;
  url?: string;
  description?: string;
  type: DataSourceType;
  enabled?: boolean;
  fileImportStatus?: ImportStatus;
  fileImportInstructions?: string;
  syncImportStatus?: ImportStatus;
  syncImportInstructions?: string;
  syncImportParams?: SyncImportParamsBase;
  syncAuthType?: "oauth";
  image?: string;
  mappedToCode?: DataSourceCode;
  /** used to lookup information and mappings about this datasource on Crypto Compare */
  ccInternalName?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface SyncImportParamsBase {
  key: ImportParamBase;
  secret?: ImportParamBase;
  passphrase?: ImportParamBase;
}

export interface ImportParamBase {
  text: string;
  order: number;
}

export enum DataSourceType {
  Exchange = "exchange",
  Blockchain = "blockchain",
  Wallet = "wallet",
  Custom = "custom",
  Import = "import",
}

// data source names
export enum DataSourceCode {
  // exchanges
  AmberApp = "amberapp",
  Bamboo = "bamboo",
  Binance = "Binance",
  BingX = "bingx",
  Bitaroo = "Bitaroo",
  Bitbns = "Bitbns",
  Bitfinex = "Bitfinex",
  Bitget = "bitget",
  Bitkub = "Bitkub",
  BitMart = "BitMart",
  BitMEX = "Bitmex",
  Bitrue = "bitrue",
  BitTrade = "bit_trade",
  BitTrex = "BitTrex",
  BlockFi = "BlockFi",
  BtcMarkets = "BTCMarkets",
  Bybit = "bybit",
  Celsius = "Celsius",
  Coinbase = "Coinbase",
  CoinEx = "CoinEx",
  CoinHarbour = "CoinHarbour",
  CoinJar = "CoinJar",
  CoinJarExchange = "CoinJarExchange",
  Cointree = "Cointree",
  CoinSpot = "CoinSpot",
  CoinStash = "CoinStash",
  CryptoDotCom = "Crypto.com",
  CryptoSpend = "cryptospend",
  DigitalSurge = "DigitalSurge",
  Elbaite = "elbaite",
  FinderEarn = "finder_earn",
  FTX = "FTX",
  Gateio = "Gateio",
  Gemini = "Gemini",
  GPIB = "gpib",
  HardBlock = "hardblock",
  HitBTC = "HitBTC",
  IndependentReserve = "IndependentReserve",
  Kraken = "Kraken",
  KuCoin = "KuCoin",
  Luno = "Luno",
  MEXC = "mexc",
  Nexo = "nexo",
  NiceHash = "NiceHash",
  OKX = "OKEX",
  Phemex = "phemex",
  Pionex = "pionex",
  Poloniex = "Poloniex",
  PrimeXBT = "primexbt",
  Swyftx = "Swyftx",
  Wealth99 = "wealth99",
  YieldApp = "yield_app",

  // wallets
  Etherscan = "Etherscan",
  Exodus = "Exodus",
  Ledger = "Ledger",
  Metamask = "Metamask",
  MyEtherWallet = "MyEtherWallet",
  Trezor = "trezor",
  Vera = "Vera",
  XTDotCom = "xtpub",
  Yoroi = "yoroi",

  // blockchains
  Bitcoin = "Bitcoin",
  BSC = "binance_smart_chain",
  Celo = "celo",
  Cronos = "cronos_evm",
  Ethereum = "Ethereum",
  FTM = "fantom",
  Helium = "Helium",
  Moonbeam = "moonbeam",
  Polygon = "polygon",

  // others
  OpeningBalances = "sylaOpeningBalances",
}

export enum ImportStatus {
  Off = "off",
  Alpha = "alpha",
  Beta = "beta",
  Gold = "gold",
}

export interface DataSourceSupportedInputs {
  sync: ImportStatus | undefined;
  file: ImportStatus | undefined;
}
export const getDataSourceSupportedInputs = ({
  fileImportStatus,
  syncImportStatus,
}: {
  fileImportStatus?: ImportStatus;
  syncImportStatus?: ImportStatus;
}): DataSourceSupportedInputs => ({
  file: (fileImportStatus ?? "off") == "off" ? undefined : fileImportStatus,
  sync: (syncImportStatus ?? "off") == "off" ? undefined : syncImportStatus,
});
